/**
 * The app-wide AI assistant (`POST /api/assistant/turn`). A turn streams NDJSON: `status` lines while
 * the assistant works, then one `reply` or `error`. Everything is normalised here, at the boundary:
 * a view, draft or action of a shape this build does not expect is dropped, never handed on.
 */
import { ApiError } from './api';
import { streamNdjsonTurn } from './ndjsonTurn';
import type {
  AssistantAction,
  AssistantChoice,
  AssistantDraft,
  AssistantQuestion,
  AssistantReply,
  AssistantTurnRequest,
  AssistantView,
} from '../types/assistant';

const ENDPOINT = '/api/assistant/turn';

/** The API gives up on a turn at 150 seconds; this covers a stalled connection, headers and stream together. */
export const ASSISTANT_TURN_TIMEOUT_MS = 180_000;

/** The longest message the coach may type (the API's limit). */
export const ASSISTANT_MAX_MESSAGE_LENGTH = 800;

/** Earlier messages sent back each turn (the API keeps this many). */
export const ASSISTANT_MAX_HISTORY = 12;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && v.trim() !== '') : [];

/**
 * A same-site path only ("/player-database?level=d1"), as `pathname + search + hash`, or null. Browsers strip tab,
 * CR and LF from URLs, so "/\t/evil.com" would become "//evil.com": any control character or whitespace is refused
 * first, and so is any dot segment; then the path must resolve to this origin and stay a single-slash path.
 */
export const safePath = (value: unknown): string | null => {
  if (typeof value !== 'string' || value === '' || !value.startsWith('/')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F\s]/.test(value) || value.includes('\\')) return null;
  // Dot segments are resolved away, so "/.//evil.com" would come out as the protocol-relative "//evil.com".
  if (/(^|\/)\.{1,2}(\/|$|[?#])/.test(value)) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  const out = url.pathname + url.search + url.hash;
  return url.origin === origin && out.startsWith('/') && !out.startsWith('//') ? out : null;
};

export const normalizeAction = (raw: unknown): AssistantAction | null => {
  if (!isRecord(raw)) return null;
  const kind = str(raw.kind);
  const label = str(raw.label);
  if (!kind || !label) return null;
  return {
    kind,
    label,
    targetId: str(raw.targetId),
    args: raw.args ?? null,
    enabled: raw.enabled !== false,
    disabledReason: str(raw.disabledReason),
  };
};

const actions = (value: unknown): AssistantAction[] =>
  Array.isArray(value) ? value.map(normalizeAction).filter((a): a is AssistantAction => a !== null) : [];

export const normalizeView = (raw: unknown): AssistantView | null => {
  if (!isRecord(raw)) return null;
  const kind = str(raw.kind);
  if (!kind) return null;
  return {
    id: str(raw.id) ?? '',
    kind,
    title: str(raw.title) ?? '',
    total: num(raw.total),
    showAllPath: safePath(raw.showAllPath),
    data: raw.data ?? null,
    actions: actions(raw.actions),
  };
};

export const normalizeDraft = (raw: unknown): AssistantDraft | null => {
  if (!isRecord(raw)) return null;
  const kind = str(raw.kind);
  if (!kind) return null;
  return { id: str(raw.id) ?? '', kind, summary: str(raw.summary) ?? '', payload: raw.payload ?? null };
};

const normalizeChoice = (raw: unknown): AssistantChoice | null => {
  if (!isRecord(raw)) return null;
  const label = str(raw.label);
  return label ? { label, targetKind: str(raw.targetKind), targetId: str(raw.targetId) } : null;
};

const normalizeQuestion = (raw: unknown): AssistantQuestion | null => {
  if (!isRecord(raw)) return null;
  const text = str(raw.text);
  if (!text) return null;
  const choices = Array.isArray(raw.choices)
    ? raw.choices.map(normalizeChoice).filter((c): c is AssistantChoice => c !== null)
    : [];
  return { text, choices };
};

/** An index into a list, or null when it does not point at a member. */
const indexInto = (value: unknown, length: number): number | null => {
  const n = num(value);
  return n !== null && Number.isInteger(n) && n >= 0 && n < length ? n : null;
};

export const normalizeAssistantReply = (raw: unknown): AssistantReply => {
  const body = isRecord(raw) ? raw : {};
  const views = Array.isArray(body.views)
    ? body.views.map(normalizeView).filter((v): v is AssistantView => v !== null)
    : [];
  const drafts = Array.isArray(body.drafts)
    ? body.drafts.map(normalizeDraft).filter((d): d is AssistantDraft => d !== null)
    : [];
  const primaryDraft = indexInto(body.primaryDraft, drafts.length);
  return {
    message: str(body.message),
    views,
    drafts,
    // One thing in the panel: a draft the answer is wins over a view.
    primaryView: primaryDraft === null ? indexInto(body.primaryView, views.length) : null,
    primaryDraft,
    suggestions: strings(body.suggestions).slice(0, 4),
    question: normalizeQuestion(body.question),
  };
};

/** A turn that failed, in words the coach can read. */
export class AssistantTurnError extends Error {
  code: string;
  /** True when the server refused the turn before it started (an HTTP error, not a line in the stream). */
  refused: boolean;

  constructor(code: string, message: string, refused = false) {
    super(message);
    this.name = 'AssistantTurnError';
    this.code = code;
    this.refused = refused;
  }
}

/** Refusals of a message's files, worded by the server for the coach: shown as they are. */
// Refusals about the files on a turn (before any work): the server's words, and the files stay for a resend.
const ATTACHMENT_CODES = new Set(['invalid_attachment', 'attachments_rate_limited', 'attachments_busy']);

const FALLBACK_MESSAGE = 'The assistant could not answer just now. Try again in a moment.';

const errorFrom = (err: unknown): AssistantTurnError => {
  if (err instanceof AssistantTurnError) return err;
  if (err instanceof ApiError) {
    const body = isRecord(err.response) ? err.response : {};
    const code = str(body.code);
    const message = str(body.message);
    // Checked before the status: a 429 for too many files has its own words, not the turn limit's.
    if (code && ATTACHMENT_CODES.has(code) && message) return new AssistantTurnError(code, message, true);
    if (err.statusCode === 429) {
      return new AssistantTurnError('rate_limited', "You've asked a lot in the last hour. Try again a little later.", true);
    }
    if (err.statusCode === 408) return new AssistantTurnError('timeout', 'That took too long. Try again.', true);
    if (err.statusCode === 404) return new AssistantTurnError('unavailable', 'The assistant is not available right now.', true);
    return new AssistantTurnError(code ?? 'ai_failed', message ?? FALLBACK_MESSAGE, true);
  }
  return new AssistantTurnError('ai_failed', FALLBACK_MESSAGE);
};

/**
 * One turn. `onStatus` hears each step ("Searching D1 shortstops") as it happens. Resolves with the
 * reply; rejects with `AssistantTurnError`, or an `AbortError` when `signal` aborts.
 */
export const streamAssistantTurn = async (
  request: AssistantTurnRequest,
  onStatus: (text: string) => void,
  signal?: AbortSignal,
  headers?: Record<string, string>
): Promise<AssistantReply> => {
  return streamNdjsonTurn<AssistantReply>({
    endpoint: ENDPOINT,
    body: request,
    timeoutMs: ASSISTANT_TURN_TIMEOUT_MS,
    parseReply: normalizeAssistantReply,
    serverError: (code, message) => new AssistantTurnError(code ?? 'ai_failed', message ?? FALLBACK_MESSAGE),
    failure: errorFrom,
    timeout: () => new AssistantTurnError('timeout', 'That took too long. Try again.'),
    onStatus,
    signal,
    headers,
  });
};
